import AuditHistoryDialog from "@/components/AuditHistoryDialog";
import EvidenceGatheringStep from "@/components/EvidenceGatheringStep";
import OutlineCreationView from "@/components/OutlineCreationView";
import PageLayout from "@/components/PageLayout";
import PicoExtractionView from "@/components/PicoExtractionView";
import QualityAssurance from "@/components/QualityAssurance";
import ReportWritingView from "@/components/ReportWritingView";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import WorkflowStepper from "@/components/WorkflowStepper";
import { getAssistantStepConfig } from "@/config/assistantConfig";
import { getVisibleSteps, getWorkflowSteps } from "@/config/projectTypeConfig";
import { AI_MODELS } from "@/constants/aiModels";
import { ProjectDetailProvider } from "@/contexts/ProjectDetailContext";
import { useChatThreads } from "@/hooks/useChatThreads";
import { useDocuments } from "@/hooks/useDocumentMetadata";
import { useProjectPermissions } from "@/hooks/useProjectPermissions";
import { useProject, useProjectDocumentIds, useProjectTemplate, useTemplateDocuments, useUpdateProject } from "@/hooks/useProjects";
import { ApiError } from "@/services/api";
import { projectsApi } from "@/services/projectsApi";
import { PicoExtraction, PicoSubgroup, TemplateDocumentData } from "@/types/models";
import type { MentionProvider } from "@lumanity/ui/chat";
import { AssistantWidget } from "@lumanity/ui/chat";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Clock, FileText, History, Maximize2, Minimize2, PenTool, Trash2, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { withoutPendingSelections } from "@/lib/extractionSelection";

// Statuses that used to be a real workflow step but were retired (e.g. "Report Writing",
// removed per #7809) resolve to the last visible step, since those projects had already
// passed every remaining stage. Any other unrecognised status (e.g. a brand-new project's
// "Document Upload" default) is not-yet-started and resolves to the first step.
const RETIRED_STATUSES = new Set([]);
function resolveStageIndex(status: string | null | undefined, stageIndex: Record<string, number>, lastVisible: number): number {
  if (status && status in stageIndex) return stageIndex[status];
  return RETIRED_STATUSES.has(status ?? "") ? lastVisible : 0;
}

const STAT_CARD_COLOR = "bg-brand-teal";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditProject } = useProjectPermissions();
  const { data: project, isLoading } = useProject(id);
  const updateProject = useUpdateProject();
  const { data: documents } = useDocuments();
  const { data: projectDocumentIds } = useProjectDocumentIds(id);
  const queryClient = useQueryClient();

  const canDelete = canEditProject(project);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [deleteBlockMessage, setDeleteBlockMessage] = useState<string | undefined>(undefined);

  const handleDeleteClick = () => {
    setDeleteBlocked(false);
    setDeleteBlockMessage(undefined);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    setIsDeleting(true);
    setDeleteBlocked(false);
    setDeleteBlockMessage(undefined);
    try {
      await projectsApi.delete(id);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate("/projects");
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setDeleteBlocked(true);
        setDeleteBlockMessage("Cannot delete this project because it has active jobs running. Please wait for them to complete or cancel them first.");
      } else {
        setDeleteBlocked(true);
        setDeleteBlockMessage("Failed to delete the project. Please try again.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const [activeStage, setActiveStage] = useState(0);
  const [picoExtractions, setPicoExtractions] = useState<PicoExtraction[]>([]);
  const [templateStatuses, setTemplateStatuses] = useState<Record<string, string>>({});
  const [chatDocumentIds, setChatDocumentIds] = useState<string[] | null>(null);
  const [stepStateNote, setStepStateNote] = useState<string | null>(null);
  const [mentionProvider, setMentionProvider] = useState<MentionProvider | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [auditHistoryOpen, setAuditHistoryOpen] = useState(false);
  const modelDisplayName = AI_MODELS.find((m) => m.value === project?.model)?.displayName ?? project?.model;

  // Fetch project template and persisted extractions
  const { data: activeTemplate } = useProjectTemplate(id);
  const templateId = activeTemplate?.template?.id;
  const { data: persistedDocuments } = useTemplateDocuments(id, templateId);

  // Derive workflow steps from the project's template type
  const allSteps = getWorkflowSteps(project?.projectType);
  const visibleSteps = getVisibleSteps(allSteps);
  const stageIndex = Object.fromEntries(allSteps.map((s, i) => [s.key, i]));
  const lastVisible = visibleSteps.length - 1;
  const stageNames = allSteps.map((s) => s.key);
  const continueLabel = visibleSteps[activeStage]?.continueLabel ?? 'Continue';
  const pageTitle = visibleSteps[activeStage]?.pageTitle ?? visibleSteps[activeStage]?.label ?? '';
  const activeStepKey = visibleSteps[activeStage]?.key;

  // Which chat thread is active per stage — defaults to the stage's original
  // deterministic thread id; "start new chat" overrides it with a fresh one.
  const [threadOverride, setThreadOverride] = useState<Record<string, string>>({});
  const defaultThreadId = activeStepKey ? `project-${id}-${activeStepKey}` : undefined;
  const threadId = activeStepKey ? threadOverride[activeStepKey] ?? defaultThreadId : undefined;

  const { data: allThreads = [], refetch: refetchThreads } = useChatThreads(id);
  const stepThreads = useMemo(() => {
    if (!defaultThreadId) return [];
    const matches = allThreads.filter(
      (t) => t.id === defaultThreadId || t.id.startsWith(`${defaultThreadId}-`)
    );
    // A just-started new chat has no DB row yet (threads are created lazily
    // on first send) — synthesize a placeholder so it shows up immediately.
    if (threadId && threadId !== defaultThreadId && !matches.some((t) => t.id === threadId)) {
      const now = new Date().toISOString();
      matches.unshift({ id: threadId, title: "New chat", createdAt: now, updatedAt: now });
    }
    return matches.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [allThreads, defaultThreadId, threadId]);

  const handleNewChat = useCallback(() => {
    if (!activeStepKey || !defaultThreadId) return;
    setThreadOverride((prev) => ({ ...prev, [activeStepKey]: `${defaultThreadId}-${crypto.randomUUID()}` }));
  }, [activeStepKey, defaultThreadId]);

  const handleSelectThread = useCallback((selectedId: string) => {
    if (!activeStepKey) return;
    setThreadOverride((prev) => ({ ...prev, [activeStepKey]: selectedId }));
  }, [activeStepKey]);

  const handleStageComplete = (currentStageIndex: number) => {
    const nextStageIndex = currentStageIndex + 1;
    const nextStatus = stageNames[nextStageIndex];
    if (nextStatus) {
      const projectStageIndex = resolveStageIndex(project?.status, stageIndex, lastVisible);
      if (nextStageIndex > projectStageIndex) {
        updateProject.mutate({ id, status: nextStatus });
      }
      setActiveStage(Math.min(nextStageIndex, visibleSteps.length - 1));
    }
  };

  const goToStep = (stepKey: string) => {
    const index = visibleSteps.findIndex((s) => s.key === stepKey);
    if (index >= 0) setActiveStage(index);
  };

  // Sync activeStage whenever the project status or workflow type changes
  useEffect(() => {
    if (project?.status) {
      const steps = getWorkflowSteps(project?.projectType);
      const idx = Object.fromEntries(steps.map((s, i) => [s.key, i]));
      const lastVisible = getVisibleSteps(steps).length - 1;
      setActiveStage(resolveStageIndex(project.status, idx, lastVisible));
    }
  }, [project?.status, project?.projectType]);

  // Reopen section when navigating between stages
  useEffect(() => {
    setIsExpanded(true);
  }, [activeStage]);

  // Steps that narrow the chat's document scope / state note / mention provider
  // set these themselves while mounted. Reset on *cleanup* (leaving the old step),
  // not in the effect body (entering the new one) — the new step's own mount
  // effect runs before this one (child effects fire before parent effects in the
  // same commit), so resetting in the body would race and immediately stomp
  // whatever the new step just set. Resetting on cleanup fires while leaving the
  // old step, entirely before the new step's setup effects run.
  useEffect(() => {
    return () => {
      setChatDocumentIds(null);
      setStepStateNote(null);
      setMentionProvider(null);
    };
  }, [activeStepKey]);

  // Load persisted extractions from database
  useEffect(() => {
    if (!persistedDocuments || !documents || documents.length === 0) return;

    // Group records by document_id
    const groupedByDocument = new Map<string, TemplateDocumentData[]>();
    // Pending rows are "Select from Evidence Base" picks with no extraction data yet.
    for (const templateDoc of withoutPendingSelections(persistedDocuments)) {
      const records = groupedByDocument.get(templateDoc.documentId) ?? [];
      records.push(templateDoc);
      groupedByDocument.set(templateDoc.documentId, records);
    }

    // Reconstruct PicoExtraction for each document
    const loadedExtractions: PicoExtraction[] = [];
    for (const [documentId, records] of groupedByDocument) {
      const document = documents.find((d) => d.id === documentId);
      if (!document) {
        console.warn(`Document ${documentId} not found for persisted extraction`);
        continue;
      }

      // Parse each record and extract subgroup with index
      const subgroupsWithIndex = records
        .map((record) => {
          if (!record.data) return null;
          try {
            const parsed = JSON.parse(record.data);
            return {
              index: parsed.index ?? 0,
              subgroup: {
                population: parsed.population,
                intervention: parsed.intervention,
                comparator: parsed.comparator,
                outcome: parsed.outcome,
                fields: parsed.fields,
              } as PicoSubgroup,
            };
          } catch (err) {
            console.error(`Failed to parse subgroup data for document ${documentId}:`, err);
            return null;
          }
        })
        .filter((item): item is { index: number; subgroup: PicoSubgroup } => item !== null);

      // Sort by index and extract subgroups
      subgroupsWithIndex.sort((a, b) => a.index - b.index);
      const subgroups = subgroupsWithIndex.map((item) => item.subgroup);

      // Get status from first record (all should have same status per document)
      const status = records[0]?.status || "Processed";
      const createdAt = records[0]?.createdAt || new Date().toISOString();

      const extraction: PicoExtraction = {
        id: `${document.id}-${createdAt}`,
        studyName: document.title,
        authors: document.author,
        countries: [document.country],
        year: document.year,
        sourceType: document.sourceType,
        sourceOrganisation: document.sourceOrganisation,
        studyId: document.id,
        status,
        subgroups,
      };

      loadedExtractions.push(extraction);
    }

    if (loadedExtractions.length > 0) {
      setPicoExtractions(loadedExtractions);
    }
  }, [persistedDocuments, documents]);

  // Documents explicitly added to this project — used as section sources throughout the workflow.
  const projectDocuments = useMemo(
    () => documents.filter((d) => projectDocumentIds?.includes(d.id) ?? false),
    [documents, projectDocumentIds]
  );

  if (isLoading || !project) {
    return (
      <PageLayout>
        <div className="text-muted-foreground py-8">Loading project...</div>
      </PageLayout>
    );
  }

  const currentStageIndex = resolveStageIndex(project.status, stageIndex, lastVisible);
  const isComplete = project.status === "Complete";
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const daysDifference = isComplete && project.daysInProgress
    ? project.daysInProgress
    : Math.floor((Date.now() - new Date(project.createdAt).getTime()) / MS_PER_DAY);

  const daysLabel = isComplete ? "Days taken to complete" : "Days in progress";

  const publicationsCount = projectDocumentIds?.length ?? 0;

  const statCards = [
    { id: "publications", icon: FileText, value: publicationsCount, label: "Publications", color: STAT_CARD_COLOR },
    { id: "team-members", icon: Users, value: project.teamMembers, label: "Team Members", color: STAT_CARD_COLOR },
    { id: "days", icon: Clock, value: daysDifference, label: daysLabel, color: STAT_CARD_COLOR },
  ];

  const stepAssistantConfig = activeStepKey ? getAssistantStepConfig(activeStepKey, project?.projectType) : undefined;

  // Only mention fields that have actually been entered on the project.
  const projectContextParts: string[] = [];
  if (project.name) projectContextParts.push(`Project: ${project.name}`);
  if (project.therapyArea) projectContextParts.push(`Therapy Area: ${project.therapyArea}`);
  if (project.condition) projectContextParts.push(`Condition: ${project.condition}`);
  if (project.objective) projectContextParts.push(`Objective: ${project.objective}`);
  const projectContextNote = projectContextParts.length > 0 ? `${projectContextParts.join("; ")}.` : null;

  const contextNotes = [projectContextNote, stepStateNote].filter(Boolean).join("\n\n");
  const effectiveSystemPrompt = contextNotes
    ? `${stepAssistantConfig?.systemPrompt ?? ""}\n\n---\nCurrent project state (context for you, not shown to the user):\n${contextNotes}\n---`
    : stepAssistantConfig?.systemPrompt;

  return (
    <PageLayout isExpanded={!isExpanded}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 mb-4 lg:grid-cols-[1fr_auto_1fr]">
          <Button
            type="button"
            variant="link"
            onClick={() => navigate('/projects')}
            className="text-brand-teal p-0 h-auto gap-1 shrink-0 col-start-1 row-start-1 justify-self-start"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Button>

          {/* Workflow Stepper */}
          <div className="flex min-w-0 justify-center col-span-2 row-start-2 lg:col-span-1 lg:col-start-2 lg:row-start-1">
            <WorkflowStepper
              stages={visibleSteps.map((s) => s.label)}
              activeStage={activeStage}
              currentStage={currentStageIndex}
              onStageClick={setActiveStage}
            />
          </div>

          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 col-start-2 row-start-1 justify-self-end lg:col-start-3"
              title={isExpanded ? "Collapse" : "Expand to full screen"}
            >
              {isExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </Button>
          </CollapsibleTrigger>
        </div>

        {/* Project Header */}
        <CollapsibleContent className="overflow-hidden data-[state=closed]:motion-safe:animate-collapsible-up data-[state=open]:motion-safe:animate-collapsible-down">
          <div className="flex flex-col md:flex-row gap-4 pb-6">
            <Card className="flex flex-col flex-1 p-6">
              <div className="flex-1">
                <div className="flex flex-col-reverse md:flex-row items-start justify-between">
                  <div className="flex items-center gap-3 mb-3">
                    <h1 className="text-2xl font-bold text-foreground">{project.name}</h1>
                    {project.clientNames.map((name) => (
                      <Badge key={name} variant="outline">{name}</Badge>
                    ))}
                    {project.model && <Badge variant="default">{modelDisplayName}</Badge>}
                  </div>
                  <div className="self-end ml-4 mb-2 md:mb-0 flex items-center justify-end flex-wrap gap-2">
                    <Button variant="outline" onClick={() => setAuditHistoryOpen(true)}>
                      <History className="w-4 h-4" />
                      View Audit History
                    </Button>
                    {canEditProject(project) && (
                      <Button variant="brand2" onClick={() => navigate(`/projects/${id}/edit`)}>
                        <PenTool />
                        Update Project Setup
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="destructive" onClick={handleDeleteClick}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-1 text-sm border-l-2 border-brand-teal pl-3">
                  <table>
                    <tbody>
                      <tr className="align-top">
                        <th scope="row" className="font-normal text-left"><span className="text-muted-foreground w-24 inline-block">Therapy Area:</span></th>
                        <td><p className="mb-2"><span className="font-medium">{project.therapyArea}</span></p></td>
                      </tr>
                      <tr className="align-top">
                        <th scope="row" className="font-normal text-left"><span className="text-muted-foreground w-24 inline-block">Condition:</span></th>
                        <td><p className="mb-2"><span className="font-medium">{project.condition}</span></p></td>
                      </tr>
                      <tr className="align-top">
                        <th scope="row" className="font-normal text-left"><span className="text-muted-foreground w-24 inline-block">Objective:</span></th>
                        <td><p><span className="font-medium">{project.objective}</span></p></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="mt-3 text-sm text-muted-foreground flex items-center gap-1">
                💡 Tip: Use the workflow stepper above to navigate between stages. Click any stage to view its details and track your progress.
              </p>
            </Card>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 gap-2">
              {statCards.map((s) => (
                <div
                  key={s.id}
                  className={`${s.color} text-white px-4 py-2 flex items-center gap-2 min-w-[180px] rounded`}
                >
                  <s.icon className="w-5 h-5 shrink-0" />
                  <span className="text-lg font-semibold leading-tight">{s.value}</span>
                  <span className="text-xs opacity-80 whitespace-nowrap">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        title="Delete Project"
        description="This will permanently delete the project and all its associated data including documents, templates, extractions, and reports. This action cannot be undone."
        itemName={project?.name}
        isBlocked={deleteBlocked}
        blockMessage={deleteBlockMessage}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Stage Content */}
      <ProjectDetailProvider value={{ projectId: id, project, projectDocuments, picoExtractions, setPicoExtractions, activeStage, handleStageComplete, goToStep, continueLabel, pageTitle, templateStatuses, setTemplateStatuses, setChatDocumentIds, setStepStateNote, setMentionProvider }}>
        {activeStepKey === "Evidence Gathering" && <EvidenceGatheringStep />}
        {activeStepKey === "Extraction" && <PicoExtractionView />}
        {activeStepKey === "Outline Creation" && <OutlineCreationView />}
        {activeStepKey === "Quality Assurance" && <QualityAssurance />}
        {activeStepKey === "Report Writing" && <ReportWritingView />}
      </ProjectDetailProvider>

      {activeStepKey && stepAssistantConfig && (
        <AssistantWidget
          title={stepAssistantConfig.title}
          subtitle={activeStepKey}
          threadId={threadId}
          context={{ projectId: id, ...(chatDocumentIds ? { documentIds: chatDocumentIds } : {}) }}
          suggestedQuestions={stepAssistantConfig.suggestedQuestions}
          introMessage={stepAssistantConfig.introMessage}
          placeholder={stepAssistantConfig.placeholder}
          headerClassName="border-b-0 bg-gradient-to-r from-blue-600 to-indigo-600"
          iconWrapperClassName="bg-white/20 text-white"
          titleClassName="text-white"
          subtitleClassName="text-blue-100"
          closeButtonClassName="text-white"
          sendButtonClassName="bg-blue-600 hover:bg-blue-700 text-white"
          userBubbleClassName="bg-blue-600 text-white"
          avatarClassName="bg-blue-600 text-white"
          buttonClassName="bg-blue-600 hover:bg-blue-700"
          systemPrompt={effectiveSystemPrompt}
          mentionProvider={mentionProvider ?? undefined}
          threads={stepThreads}
          activeThreadId={threadId}
          onNewChat={handleNewChat}
          onSelectThread={handleSelectThread}
          onThreadUpdated={() => refetchThreads()}
        />
      )}
      <AuditHistoryDialog
        open={auditHistoryOpen}
        onClose={() => setAuditHistoryOpen(false)}
        projectId={id}
        stages={allSteps}
        defaultStage={activeStepKey}
      />
    </PageLayout>
  );
};

export default ProjectDetail;
